import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@design-lab/shared/graphql";
import { useAtom } from "jotai";
import { shopifyRecipeLoadingAtom } from "@design-lab/shared/state";
import { useNotification } from "@design-lab/shared/ui/notification";
import { trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { useEditingRecipeId } from "@design-lab/shared/lib/config";
import { CreateShopifyRecipeDocument } from "graphql-api/operations";
import type { CreateShopifyRecipeInput } from "graphql-api/types";

const REDIRECT_DELAY_MS = 3000;

/**
 * Hook for creating or updating Shopify recipes from the current design project.
 *
 * Manages the mutation state, loading indicator, and user notifications
 * for the recipe creation/update flow.
 *
 * @returns Object containing:
 *   - createShopifyRecipe: Function to trigger recipe creation/update (requires storeId)
 *   - isLoading: Whether the mutation is in progress
 *   - isEditing: Whether we're updating an existing recipe
 */
export function useCreateShopifyRecipe() {
  const [createRecipeMutation] = useMutation(CreateShopifyRecipeDocument);
  const [isLoading, setIsLoading] = useAtom(shopifyRecipeLoadingAtom);
  const { success, error } = useNotification();
  const { projectUuid } = useDesignProjectSync();
  const editingRecipeId = useEditingRecipeId();
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEditing = Boolean(editingRecipeId);

  useEffect(
    () => () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    },
    []
  );

  /**
   * Creates or updates Shopify recipes from the current design project.
   *
   * @param shopifyStoreId - The ID of the Shopify store (required)
   * @returns Object with success status and recipe IDs if successful
   */
  const createShopifyRecipe = useCallback(
    async (shopifyStoreId: string) => {
      if (!projectUuid) {
        error({
          title: "Error",
          description: "Please save your project first before creating a Shopify recipe."
        });
        return { success: false };
      }

      if (!shopifyStoreId) {
        error({
          title: "Error",
          description: "Please select a Shopify store."
        });
        return { success: false };
      }

      trackDesignLabCtaClicked("create_shopify_recipe");
      setIsLoading(true);

      try {
        const input: CreateShopifyRecipeInput = {
          designProjectUuid: projectUuid,
          shopifyStoreId,
          replaceRecipeId: editingRecipeId ?? undefined
        };

        const result = await createRecipeMutation({
          variables: { input }
        });

        const response = result.data?.createShopifyRecipe;

        if (response?.success) {
          const actionText = isEditing ? "updated" : "created";
          const recipeId = response.recipeIds?.[0];
          success({
            title: "Success",
            description: `Shopify recipe ${actionText} successfully! Redirecting to recipe...`
          });
          redirectTimeoutRef.current = setTimeout(() => {
            if (recipeId) {
              window.location.href = `/admin/tools/shopify_stores/${shopifyStoreId}/shopify_print_recipes/${recipeId}`;
            } else {
              window.location.href = "/design-lab";
            }
          }, REDIRECT_DELAY_MS);
          return { success: true, recipeIds: response.recipeIds };
        }
        error({
          title: isEditing ? "Failed to Update Recipe" : "Failed to Create Recipe",
          description: response?.errorMessage || "An unexpected error occurred."
        });
        return { success: false };
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred.";
        error({
          title: "Error",
          description: errorMessage
        });
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [createRecipeMutation, projectUuid, editingRecipeId, isEditing, setIsLoading, success, error]
  );

  return {
    createShopifyRecipe,
    isLoading,
    isEditing
  };
}
